import { Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import { LabeledTagGrid, type LabeledTagGridRow } from 'src/components'

import { groupPermissionsByResource } from './permissionGrouping'

const { Text } = Typography

type Props = {
  permissionCodes: readonly string[]
}

/**
 * Renders one app's permissions as a `resource → action tags` grid. Used
 * from both credential detail pages: for public-access it lives inside a
 * tab (one per bound app); for app credentials it's the only panel.
 */
export const AppPermissionPanel = ({ permissionCodes }: Props) => {
  const { t } = useTranslation()
  const groups = groupPermissionsByResource(permissionCodes)

  if (permissionCodes.length === 0 || groups.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t('workspace.credentials.noPermissions')}
      </Text>
    )
  }

  const rows: LabeledTagGridRow[] = groups.map(
    ({ resource, resourceLabel, perms }) => ({
      key: resource,
      label: resourceLabel,
      items: perms.map((perm) => ({
        key: perm.code,
        label: perm.actionLabel,
        tooltip: `${resourceLabel} · ${perm.actionLabel}`
      }))
    })
  )

  return (
    <LabeledTagGrid
      rows={rows}
      summary={t('workspace.credentials.appSummary', {
        permissions: permissionCodes.length,
        resources: groups.length
      })}
    />
  )
}
